import { DateTime } from 'luxon'
import { Timeline } from './Timeline'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTw } from '../../../../../../../utils/Styles'
import { localizeString } from '../../../../../../../utils'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline'
import { StatusPageQuery } from '../../../StatusPage.generated'
import { Select } from '../../../../../../../utils/Form'

type Props = {
  day: string
  eventId?: string
  hasPrev: boolean
  hasNext: boolean
  componentId: string
  onPrev: () => void
  onNext: () => void
  displayMode: string
  historyItem: StatusPageQuery['Profile']['statusPage']['components'][0]['eventsHistory'][0]
}

export function StatusDetail({
  day,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  eventId,
  historyItem,
  displayMode,
  componentId,
}: Props) {
  const tw = useTw()
  const navigate = useNavigate()
  const [t] = useTranslation('common')

  const issue =
    (eventId && historyItem.events.find((_) => _._id === eventId)) ||
    historyItem.events[0]

  if (!issue) return null

  const startedAt = DateTime.fromISO(issue.startedAt)
  const finishedAt = issue.finishedAt
    ? DateTime.fromISO(issue.finishedAt)
    : null

  return (
    <div>
      <div className={tw`pt-8 flex flex-col divide-y divide-gray-300`}>
        <div className={tw`space-y-2`}>
          {displayMode === 'HISTORY' ? (
            <div className={tw`flex justify-between items-center pb-2`}>
              <ChevronLeftIcon
                onClick={() => hasPrev && onPrev()}
                className={tw`h-8 w-8 select-none
                  ${
                    hasPrev ? 'text-foreground cursor-pointer' : 'text-gray-400'
                  }
                `}
              />
              <time className={tw`text-sm text-gray-600`} dateTime={day}>
                {DateTime.fromISO(day).toLocaleString(DateTime.DATE_MED)}
              </time>
              <ChevronRightIcon
                onClick={() => hasNext && onNext()}
                className={tw`h-8 w-8 select-none
                  ${
                    hasNext ? 'text-foreground cursor-pointer' : 'text-gray-400'
                  }
                `}
              />
            </div>
          ) : null}
          {historyItem.events.length > 1 ? (
            <div className={tw`pb-2`}>
              <Select
                value={issue._id}
                onChange={({ target }) =>
                  navigate(`/status-page/${componentId}/${day}/${target.value}`)
                }
              >
                {historyItem.events.map((event) => (
                  <option key={event._id} value={event._id}>
                    {[
                      startedAt.toLocaleString(DateTime.TIME_SIMPLE),
                      localizeString(event.title),
                    ].join(' - ')}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          <h1>{localizeString(issue.title)}</h1>
          <p className={tw`text-sm text-gray-500`}>
            {localizeString(issue.description)}
          </p>
          <div className={tw`text-sm`}>
            <p className={tw`text-sm space-x-2`}>
              <strong>{t('started_at')}</strong>
              <time dateTime={startedAt.toISO()} className={tw`text-gray-500`}>
                {startedAt.toLocaleString(DateTime.DATETIME_MED)}
              </time>
            </p>
            {issue.finishedAt ? (
              <p className={tw`text-sm space-x-2`}>
                <strong>{t('finished_at')}</strong>
                <time
                  dateTime={finishedAt.toISO()}
                  className={tw`text-gray-500`}
                >
                  {finishedAt.toLocaleString(DateTime.DATETIME_MED)}
                </time>
              </p>
            ) : null}
          </div>
        </div>
        <div className={tw`pt-4 mt-4`}>
          <Timeline event={issue} />
        </div>
      </div>
    </div>
  )
}
